import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import {
  declineEstimate,
  getAppointment,
  getEstimateDepositInfo,
  getEstimatePdfAsync,
} from 'api';

import {
  ApproveEstimateModal,
  Breadcrumbs,
  Button,
  ButtonLink,
  ConfirmationButton,
  InfoBoxItem,
  PageHeader,
  PaymentSummary,
  RequestTitle,
  RichText,
  Group,
} from 'components/ui-kit';
import AttachedFormsList from 'components/attached-forms-list/index';
import PayEstimateDepositModal from 'components/pay-estimate-deposit-modal';

import { useApp, useModal } from 'hooks';
import routes from 'router/routes';

import { getFullName } from 'helpers/business.helper';
import { formatDate } from 'helpers/datetime.helper';
import { depositAmountTypes, estimateApprovalTypes } from 'helpers/enums';

import RequestFeedbackModal from './feedback-modal';
import RequestChangesModal from './request-changes-modal';

import { APPROVAL_STATUSES, getModalContent } from './utils';

import * as S from './styles';

const ESTIMATE_STATUS_COPIED_TO_JOB = 5;
// Sent and Requested changes: the API answers an estimate only once it was sent and until it is answered.
const ESTIMATE_STATUSES_AWAITING_ANSWER = [1, 2];
const formatMoney = (value) => `$${Number(value || 0).toFixed(2)}`;

const RequestItemDetails = () => {
  const { id } = useParams();
  const { portal, user } = useApp();
  const { showModal } = useModal();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [lineItems, setLineItems] = useState([]);
  const hasUserEditedLineItems = useRef(false);

  const stripeRedirectResult = searchParams.get('stripe_result');

  const clearStripeResultParam = useCallback(() => {
    if (!searchParams.get('stripe_result')) {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('stripe_result');
    setSearchParams(nextParams);
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    let invalidatorTimeout;

    if (stripeRedirectResult === 'success') {
      invalidatorTimeout = setTimeout(() => {
        queryClient.invalidateQueries(['appointment', portal?.id, id]);
        queryClient.invalidateQueries(['estimate-deposit', portal?.id, id]);
        clearStripeResultParam();
      }, 3000);
    }

    if (stripeRedirectResult === 'cancel') {
      clearStripeResultParam();
    }

    return () => {
      clearTimeout(invalidatorTimeout);
    };
  }, [stripeRedirectResult, portal?.id, id, queryClient, clearStripeResultParam]);

  const handleLineItemsChange = useCallback((updated) => {
    hasUserEditedLineItems.current = true;
    setLineItems(updated);
  }, []);

  const {
    isError, isLoading, isFetching, data: appointmentData, refetch,
  } = useQuery(
    ['appointment', portal?.id, id],
    getAppointment,
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: !!portal?.id,
    },
  );

  const declineMutation = useMutation(({ portalId, estimateId }) => (declineEstimate(portalId, estimateId)));

  const isApproved = appointmentData?.data?.approvalStatus === estimateApprovalTypes.approved.value;
  const isNotYetApproved = appointmentData?.data?.approvalStatus === estimateApprovalTypes.notYetApproved.value;
  const isCopiedToJob = appointmentData?.data?.estimateStatus === ESTIMATE_STATUS_COPIED_TO_JOB;

  const acceptedOptionalLineItemIds = lineItems
    .filter((lineItem) => lineItem.optionalState?.isAccepted)
    .map((lineItem) => lineItem.id)
    .join(',');

  const { data: depositRes } = useQuery(
    ['estimate-deposit', portal?.id, id, acceptedOptionalLineItemIds],
    getEstimateDepositInfo,
    {
      refetchOnWindowFocus: false,
      retry: false,
      keepPreviousData: true,
      enabled: !!portal?.id && (isNotYetApproved || isApproved) && !isCopiedToJob,
    },
  );

  useEffect(() => {
    if (hasUserEditedLineItems.current) {
      return;
    }

    if (appointmentData?.data?.lineItems?.length) {
      const { lineItems: initialLineItems } = appointmentData.data;

      setLineItems(initialLineItems.map((item) => {
        const { optionalState } = item;

        let updatedOptionalState = null;

        if (optionalState) {
          updatedOptionalState = {
            isRecommended: optionalState.isRecommended,
            isAccepted: optionalState.isAccepted === null ? optionalState.isRecommended : optionalState.isAccepted,
          };
        }

        return {
          ...item,
          optionalState: updatedOptionalState,
          key: uuidv4(),
        };
      }));
    }
  }, [appointmentData, appointmentData?.data?.lineItems]);

  if (isError) {
    return 'Error occurred while fetching appointment';
  }

  if (isLoading || isFetching) {
    return null;
  }

  const { data: appointment } = appointmentData;

  const {
    id: appointmentId,
    title,
    estimateNumber,
    hasVersions,
    versionNumber,
    checkLists,
    approvalStatus,
    estimateStatus,
    estimateSent,
    expiresOn,
    serviceAddress,
    unitNumber,
    businessEntityType,
    taxRate,
    discount,
    appointmentNotes,
    preCalculatedSummary,
  } = appointment || {};

  const isReadOnly = approvalStatus !== estimateApprovalTypes.notYetApproved.value
    || !ESTIMATE_STATUSES_AWAITING_ANSWER.includes(estimateStatus);
  // The server declines it on its daily run; until then the date alone closes it, as the server's approve does.
  const isPastExpiry = !isReadOnly && !!expiresOn && new Date(expiresOn) <= new Date();

  // Past the date nothing on the page is open any more, the deposit and the optional items included.
  const requiredDeposit = !isReadOnly && !isPastExpiry && depositRes?.data?.isRequired
    ? {
      amount: depositRes.data.amount,
      amountType: depositRes.data.amountType,
      rawAmount: depositRes.data.rawAmount,
    }
    : null;

  const handleDecline = async () => {
    await declineMutation.mutateAsync({ estimateId: appointmentId, portalId: portal?.id });
    await refetch();
  };

  const afterDecline = () => {
    setTimeout(() => {
      showModal(RequestFeedbackModal, { companyName: portal?.company?.businessName });
    }, 300);
  };

  return (
    (!!appointment
      && (
      <S.Box>
        <Breadcrumbs
          items={[
            { text: routes.requests.title, to: `../${routes.requests.path}` },
            { text: title, to: '#' },
          ]}
          gap="1rem"
        />

        <S.ContentBox>
          <PageHeader
            title={(
              <RequestTitle
                baseFontSize={26}
                title={title}
                number={estimateNumber}
                hasVersions={hasVersions}
                versionNumber={versionNumber}
              />
            )}
            actionItems={(
              <ButtonLink
                icon={<S.DownloadIcon />}
                newTab
                href={getEstimatePdfAsync(portal?.id, id, lineItems)}
              >
                Download Estimate
              </ButtonLink>
            )}
          />

          <S.Section>
            <S.SectionTitle>
              Recipient details
            </S.SectionTitle>

            <S.SectionItems>
              <InfoBoxItem title="Name" value={getFullName(user)} showColon={false} />

              <InfoBoxItem title="Company" value={user?.ownCompanyName} showColon={false} />
            </S.SectionItems>
          </S.Section>

          <S.Divider />

          <S.Section>
            <S.SectionTitle>
              Estimate details
            </S.SectionTitle>

            <S.SectionItems>
              <InfoBoxItem
                title="Sent date"
                value={
                  estimateSent
                    ? formatDate(estimateSent)
                    : null
                }
                placeholder="n/a"
                showColon={false}
              />

              {!!expiresOn && (
                <InfoBoxItem
                  title={isReadOnly || isPastExpiry ? 'Expired on' : 'Expires on'}
                  value={formatDate(expiresOn)}
                  showColon={false}
                />
              )}

              <InfoBoxItem
                title="Status"
                value={APPROVAL_STATUSES[approvalStatus].label}
                showColon={false}
                statusColor={APPROVAL_STATUSES[approvalStatus].color}
              />

              <InfoBoxItem
                title="Service property"
                value={serviceAddress}
                description={unitNumber ? `Unit #: ${unitNumber}` : ''}
                showColon={false}
              />
            </S.SectionItems>
          </S.Section>

          {!!checkLists?.length && (
            <AttachedFormsList
              entityType={businessEntityType}
              checkLists={checkLists}
              eventId={appointmentId}
            />
          )}

          <S.Divider />

          <PaymentSummary
            lineItems={lineItems}
            taxRate={taxRate}
            discount={discount}
            withOptional={!!lineItems.filter((lineItem) => !!lineItem.optionalState).length}
            setLineItems={handleLineItemsChange}
            disabled={isReadOnly || isPastExpiry}
            preCalculatedSummary={preCalculatedSummary || {}}
            requiredDeposit={requiredDeposit}
          />

          {!!requiredDeposit && (
            <S.DepositCaption>
              {'A '}
              <S.DepositCaptionHighlight>
                {requiredDeposit.amountType === depositAmountTypes.percent
                  ? `${requiredDeposit.rawAmount}%`
                  : formatMoney(requiredDeposit.rawAmount)}
                {` deposit of ${formatMoney(requiredDeposit.amount)}`}
              </S.DepositCaptionHighlight>
              {' is required to approve this estimate.'}
            </S.DepositCaption>
          )}

          {isApproved && !isCopiedToJob && !!depositRes?.data?.isRequired && (
            <S.RightRow>
              <Button
                onClick={() => showModal(PayEstimateDepositModal, { id })}
                color="success"
              >
                {`Pay Deposit ${formatMoney(depositRes.data.amount)}`}
              </Button>
            </S.RightRow>
          )}

          {isPastExpiry && (
            <S.DepositCaption>
              {`This estimate has expired. Please contact ${portal?.company?.businessName || 'the company'} `
                + 'for an updated estimate.'}
            </S.DepositCaption>
          )}

          {!isReadOnly && !isPastExpiry && (
            <S.ButtonGroupWrapper>
              <S.RequestChangesButton
                onClick={() => {
                  showModal(RequestChangesModal, { estimateId: id });
                }}
              >
                Request changes
              </S.RequestChangesButton>

              <S.ButtonGroup>
                <ConfirmationButton
                  buttonTitle="Decline"
                  onConfirm={() => handleDecline()}
                  afterConfirm={() => afterDecline()}
                  modalContent={getModalContent(appointment?.estimateNumber, 'decline')}
                >
                  Decline
                </ConfirmationButton>

                <Button
                  onClick={() => showModal(ApproveEstimateModal, { id, estimateTitle: title, lineItems })}
                  color="success"
                >
                  Approve
                </Button>
              </S.ButtonGroup>
            </S.ButtonGroupWrapper>
          )}

          {!!appointmentNotes && (
            <Group mt={16}>
              <InfoBoxItem
                title="Estimate Notes"
                value={<RichText html={appointmentNotes} />}
                showColon={false}
              />
            </Group>
          )}
        </S.ContentBox>
      </S.Box>
      )
    )
  );
};

export default RequestItemDetails;
